import type { BadgeVariant } from "@dl/web-core-js/ui";
import type {
  ArtworkStatus,
  ArtworkSyncStatus,
  ArtworkTagType,
  ArtworkImageType,
  DecorStatus,
  DisplayCollectionImageOwner,
  JoliscreenImageStatus,
  LegacyProductionJobStatus,
  OrderFinancialStatus,
  OrderFulfillmentStatus,
  OrderHeaderDispatchStatus,
  ProductDistributionStatus,
  ProductFamily,
  ProductStatus,
  ProductionCenterType,
  ProductionJobLineStatus,
  ProductionJobStatus,
  PublisherStatus,
  PublisherType,
} from "../api";

/**
 * The colour of every status pill in the app, copied one-for-one from the
 * `switch` expressions in the Vaadin grids. Keeping them in one table is what
 * stops the same status reading green on one screen and grey on the next.
 */

export const ARTWORK_STATUS_VARIANT: Record<ArtworkStatus, BadgeVariant> = {
  ACTIVE: "success",
  INCOMPLETE: "neutral",
  TERMINATED: "error",
};

export const ARTWORK_SYNC_VARIANT: Record<ArtworkSyncStatus, BadgeVariant> = {
  ACTIVE: "success",
  SUSPENDED: "error",
};

export const ARTWORK_TAG_VARIANT: Record<ArtworkTagType, BadgeVariant> = {
  COLOR: "success",
  THEME: "info",
  KEYWORD: "neutral",
};

export const ARTWORK_IMAGE_VARIANT: Record<ArtworkImageType, BadgeVariant> = {
  HR_ORIGINAL: "success",
  HR_STANDARDIZED: "success",
  HR_CORRECTED: "success",
  THUMBNAIL: "info",
  SMALL: "info",
  MEDIUM: "info",
  LARGE: "info",
};

export const PRODUCT_STATUS_VARIANT: Record<ProductStatus, BadgeVariant> = {
  ACTIVE: "success",
  TERMINATED: "error",
};

export const DISTRIBUTION_VARIANT: Record<ProductDistributionStatus, BadgeVariant> = {
  OPEN: "success",
  RESTRICTED: "error",
};

export const PRODUCT_FAMILY_VARIANT: Record<ProductFamily, BadgeVariant> = {
  FINISHED: "success",
  GIANT: "info",
  PAPER: "neutral",
  MISC: "neutral",
  NO_PRINT: "warning",
};

export const PUBLISHER_TYPE_VARIANT: Record<PublisherType, BadgeVariant> = {
  PERSON: "info",
  GROUP: "neutral",
  ORGANIZATION: "accent",
};

export const PUBLISHER_STATUS_VARIANT: Record<PublisherStatus, BadgeVariant> = {
  ACTIVE: "success",
  TERMINATED: "error",
};

export const DECOR_STATUS_VARIANT: Record<DecorStatus, BadgeVariant> = {
  ACTIVE: "success",
  DELETED: "error",
};

export const OWNER_VARIANT: Record<DisplayCollectionImageOwner, BadgeVariant> = {
  ARTWORK: "neutral",
  PRODUCT: "success",
};

export const JOLISCREEN_VARIANT: Record<JoliscreenImageStatus, BadgeVariant> = {
  ACTIVE: "success",
  INACTIVE: "neutral",
};

export const CENTER_TYPE_VARIANT: Record<ProductionCenterType, BadgeVariant> = {
  INTERNAL: "info",
  EXTERNAL: "neutral",
};

export const JOB_STATUS_VARIANT: Record<ProductionJobStatus, BadgeVariant> = {
  PENDING: "warning",
  IN_PROGRESS: "info",
  COMPLETED: "success",
  FAILED: "error",
  CANCELED: "neutral",
  ARCHIVED: "neutral",
};

export const JOB_LINE_STATUS_VARIANT: Record<ProductionJobLineStatus, BadgeVariant> = {
  PENDING: "warning",
  READY_TO_PRINT: "info",
  PRINTED: "success",
  SHIPPED: "success",
  SUBMITTED: "success",
  FAILED: "error",
  CANCELED: "neutral",
};

export const LEGACY_JOB_VARIANT: Record<LegacyProductionJobStatus, BadgeVariant> = {
  ON_HOLD: "warning",
  IN_PROGRESS: "info",
  FAILED: "error",
  CANCELLED: "neutral",
  COMPLETED: "success",
};

export const DISPATCH_VARIANT: Record<OrderHeaderDispatchStatus, BadgeVariant> = {
  NOT_DISPATCHABLE: "neutral",
  NOT_DISPATCHED: "error",
  PARTIAL_DISPATCH: "warning",
  DISPATCHED: "success",
};

export const FULFILLMENT_VARIANT: Partial<Record<OrderFulfillmentStatus, BadgeVariant>> = {
  OPEN: "info",
  FULFILLED: "success",
  ON_HOLD: "error",
  REQUEST_DECLINED: "error",
};

export const FINANCIAL_VARIANT: Partial<Record<OrderFinancialStatus, BadgeVariant>> = {
  PAID: "success",
  AUTHORIZED: "info",
  PARTIALLY_PAID: "info",
  EXPIRED: "error",
  REFUNDED: "error",
  VOIDED: "error",
};

export const ORDER_KIND_VARIANT: Record<"DRAFT" | "ORDER", BadgeVariant> = {
  DRAFT: "neutral",
  ORDER: "success",
};
